import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, interval } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { Avatar } from '@finder/shared/components/avatar/avatar.interface';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CommonModule } from '@angular/common';
import { AvatarComponent } from '../avatar/avatar.component';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { ComponentHeaderComponent } from '../component-header/component-header.component';

@Component({
    selector: 'finder-avatar-list',
    templateUrl: './avatar-list.component.html',
    styleUrls: ['./avatar-list.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        AvatarComponent,
        InViewportDirective,
        ComponentHeaderComponent
    ]
})
export class AvatarListComponent implements OnInit, OnDestroy {

    @Input() title: any;
    @Input() avatarList: Array<Avatar>;
    @Input() disableAnimation = false;
    @Input() listAvatarWidth;

    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private viewportDetection: ViewportDetectionService,
    ) { }

    ngOnInit(): void {
        if (!this.avatarList) {
            return;
        }

        // Setup css vars
        this.setMetrics();

        this.viewportDetection.onViewportResize()
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                // Update the css vars based on viewport changes
                this.setMetrics();
            });

    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    animateAvatars() {
        if (this.disableAnimation) {
            return;
        }

        if (this.avatarList && this.avatarList.length) {
            interval(250)
                .pipe(take(this.avatarList.length))
                .subscribe((position) => {
                    this.avatarList[position].animate = true;
                });
        }
    }

    private setMetrics() {
        const metrics = this.avatarList[0]?.image?.metrics;
        if (!metrics) {
            return;
        }

        const viewport = this.viewportDetection.isMobile() ? 'mobile' : 'desktop';
        const { width, height } = metrics[viewport];

        const el = this.elementRef.nativeElement;
        el.style.setProperty('--avatar-image-max-width', `${width}px`);
        el.style.setProperty('--avatar-image-max-height', `${height}px`);

        if (this.listAvatarWidth) {
            el.style.setProperty('--avatar-list-avatar-width', this.listAvatarWidth);
        }
    }
}
